import { useLocalStorageState } from 'ahooks';
import { FormikConfig, useFormik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { Button, Input, Loading } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { useModalsContext } from '../../../../../../providers/ModalsProvider/Modals.context.ts';
import { useSocketsContext } from '../../../../../../providers/SocketsProvider/Sockets.context.ts';
import { useUserContext } from '../../../../../../providers/UserProvider/User.context.ts';
import { eUserRole } from '../../../../../../providers/UserProvider/User.enums.ts';
import { tAdvanceSearchLead } from '../../../../../AdvanceSearch/AdvanceSearchModal.types.ts';
import { chatLeadParser } from '../../../../Chat.functions.ts';
import { tFunnelInfo } from '../../../../Chat.types.ts';
import {
  PasteTruncatedWarning,
  usePasteTruncationWarning,
} from 'src/components/FieldCharCounter';
import { MAX_LEAD_TEXT_FIELD_LENGTH } from './ChatInfoAttributes.constants.ts';
import useChatInfo from './ChatInfoAttributes.hook.ts';
import { buildChatInfoAttributesSchema } from './ChatInfoAttributes.schema.ts';
import './ChatInfoAttributes.styles.scss';
import { tAttributesForm } from './ChatInfoAttributes.types.ts';
import {
  getTagName,
  mergeLocalTags,
  normalizeTagName,
  tTagEntry,
} from './ChatInfoAttributes.tag.utils.ts';
import TagButton from './components/TagButton/TagButton.component.tsx';
import TagTypeahead, {
  tTagOption,
} from './components/TagTypeahead/TagTypeahead.component.tsx';
import { useChatContext } from '../../../../Chat.hook.ts';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { getModuleName } from '../../../../Chat.utils.ts';
import LeadClosePanelRows from 'src/components/LeadClose/LeadClosePanelRows.component';
import { useLeadClose } from 'src/components/LeadClose/useLeadClose.hook';
import { toCloseLeadRef } from 'src/components/LeadClose/LeadClose.functions';

export default function ChatInfoAttributes(): JSX.Element {
  const { currentUserRole } = useUserContext();
  const { tagInputRef } = useChatContext();
  const { leadUpdatedEvent } = useSocketsContext();
  const { selectedLeadForChatModal } = useModalsContext();
  const { t } = useTranslation(['chat/chat']);
  // Bound separately: the schema's length message lives in errors/errors.
  const { t: tErrors } = useTranslation('errors/errors');
  const { trackEvent } = useAnalytics();
  // LEA-50: with `leads_lead-close` on, Ganancia is the sum of the lead's
  // sales — read-only here, next to the "Resultado" row.
  const { enabled: leadCloseEnabled } = useLeadClose();
  // Value captured on focus so the change events only fire on a real edit,
  // not on a focus/blur with no change (tab-through).
  const valueOnFocusRef = useRef<number | undefined>(undefined);
  const revenueOnFocusRef = useRef<number | undefined>(undefined);

  const [currentFunnelInfo] = useLocalStorageState<tFunnelInfo[]>(
    'funnelData',
    {
      defaultValue: [],
      listenStorageChange: true,
    },
  );

  const [showSave, setShowSave] = useState(false);
  const [selectedLead, setSelectedLead] = useState<tAdvanceSearchLead | null>(
    null,
  );
  // Deliberately outside formik: a tag commits the moment it is picked, so it
  // must not make the form dirty and raise the save button (BUG-938).
  const [tagQuery, setTagQuery] = useState('');
  const { onChatInfoAttributesSubmit, attachTag, tagOpsInFlight } =
    useChatInfo(selectedLead);
  const hasUnsavedEditsRef = useRef(false);
  const pendingLeadUpdateRef = useRef<
    Parameters<typeof chatLeadParser>[0] | null
  >(null);

  const leadFunnel = currentFunnelInfo?.find(
    (funnel) => funnel.uuid === selectedLeadForChatModal?.funnel?.uuid,
  );

  const ShowTotalAccess =
    leadFunnel?.can_view_handle ||
    currentUserRole === eUserRole.ADMIN ||
    currentUserRole === eUserRole.OWNER;

  const namePaste = usePasteTruncationWarning(MAX_LEAD_TEXT_FIELD_LENGTH);

  let value = selectedLead?.value || 0;
  let revenue = selectedLead?.revenue || 0;
  const formikConfig: FormikConfig<tAttributesForm> = {
    initialValues: {
      full_name: selectedLead?.name || '',
      email: selectedLead?.email || '',
      company: selectedLead?.company || '',
      handle: selectedLead?.handle || '',
      revenue,
      value,
    },
    validationSchema: buildChatInfoAttributesSchema(tErrors),
    onSubmit: async (values, { resetForm }) => {
      const saved = await onChatInfoAttributesSubmit(values);
      if (!saved) return;

      const pending = pendingLeadUpdateRef.current;
      pendingLeadUpdateRef.current = null;
      if (selectedLead) {
        const remoteLead =
          pending?.response?.uuid === selectedLead.id
            ? chatLeadParser(pending, selectedLead)
            : selectedLead;
        const nextLead = {
          ...remoteLead,
          name:
            values.full_name !== selectedLead.name
              ? values.full_name
              : remoteLead.name,
          email:
            values.email !== selectedLead.email
              ? values.email
              : remoteLead.email,
          company:
            values.company !== selectedLead.company
              ? values.company
              : remoteLead.company,
          value:
            values.value !== selectedLead.value
              ? values.value
              : remoteLead.value,
          revenue:
            values.revenue !== selectedLead.revenue
              ? values.revenue
              : remoteLead.revenue,
          // Tags no longer travel with the form, but a socket payload queued
          // while the form was dirty can predate a tag attached in between.
          // Keep the chips the panel is already showing.
          tags: mergeLocalTags(remoteLead.tags, selectedLead.tags),
        };
        setSelectedLead(nextLead);
        resetForm({
          values: {
            ...values,
            full_name: nextLead.name,
            email: nextLead.email,
            company: nextLead.company,
            value: Number(nextLead.value),
            revenue: Number(nextLead.revenue),
          },
        });
      }
      setShowSave(false);
    },
    enableReinitialize: true,
  };

  const formik = useFormik(formikConfig);
  useEffect(() => {
    hasUnsavedEditsRef.current = formik.dirty;
    if (!formik.dirty && pendingLeadUpdateRef.current) {
      const pending = pendingLeadUpdateRef.current;
      pendingLeadUpdateRef.current = null;
      setSelectedLead((lead) =>
        lead && lead.id === pending.response?.uuid
          ? chatLeadParser(pending, lead)
          : lead,
      );
    }
  }, [formik.dirty]);

  useEffect(() => {
    setShowSave(formik.dirty);
  }, [formik.dirty]);

  useEffect(() => {
    if (selectedLeadForChatModal && !hasUnsavedEditsRef.current) {
      pendingLeadUpdateRef.current = null;
      setSelectedLead(selectedLeadForChatModal);
    }
  }, [selectedLeadForChatModal]);

  leadUpdatedEvent.useSubscription((lead) => {
    if (selectedLeadForChatModal?.id !== lead.response?.uuid) return;
    if (hasUnsavedEditsRef.current) {
      pendingLeadUpdateRef.current = lead;
      return;
    }
    setSelectedLead((oldLead) => {
      if (!oldLead) return null;
      const parsed = chatLeadParser(lead, oldLead);
      if (tagOpsInFlight.current > 0) {
        return { ...parsed, tags: oldLead.tags };
      }
      return parsed;
    });
  });

  async function onTagSelect(option: tTagOption): Promise<void> {
    const tagName = option.value.trim();
    // The pick is the commit, so the query box empties right away — the chip
    // below is what confirms the tag, exactly as removal works today.
    setTagQuery('');
    if (!tagName) return;

    const normalizedTagName = normalizeTagName(tagName);
    if (
      (selectedLead?.tags || []).some(
        (tag) => normalizeTagName(tag as tTagEntry) === normalizedTagName,
      )
    )
      return;

    setSelectedLead((lead) =>
      lead ? { ...lead, tags: [...lead.tags, tagName] } : lead,
    );

    const attached = await attachTag(tagName);
    if (!attached) {
      setSelectedLead((lead) =>
        lead
          ? {
              ...lead,
              tags: lead.tags.filter(
                (tag) =>
                  normalizeTagName(tag as tTagEntry) !== normalizedTagName,
              ),
            }
          : lead,
      );
      return;
    }

    // Emitted from the branch that saw the server accept the tag, per the
    // outcome-events convention in CLAUDE.md.
    trackEvent('front.chat.attributes.update.tag.change', {
      displayName: 'Update etiqueta',
      description: 'Con qué frecuencia se crean o eliminan etiquetas',
      ctaType: getModuleName(),
      tagStatus: option.isNew ? 'Create' : 'Select',
    });
  }

  if (typeof value === 'string') {
    value = Number(value);
  }

  if (typeof revenue === 'string') {
    revenue = Number(revenue);
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      className="ChatInfoAttributes__Content__Container ph-no-capture"
    >
      <div className="ChatInfoAttributes__Content__Item">
        <p>{t('attributes.name')}:</p>
        <Input
          color="ghost"
          size="sm"
          name={'full_name'}
          // `max` is the HTML attribute for numeric/date inputs and is inert on a
          // text field; `maxLength` is the one that actually caps typing and paste.
          maxLength={MAX_LEAD_TEXT_FIELD_LENGTH}
          onChange={(event) => {
            namePaste.clear();
            formik.handleChange(event);
          }}
          onPaste={namePaste.onPaste}
          placeholder={t('attributes.nameLabel')}
          value={
            formik.values.full_name.includes('@lid')
              ? t('leadConfidential')
              : formik.values.full_name
          }
          className="ChatInfoAttributes__Content__Item__Value"
        />
      </div>
      {formik.errors.full_name && formik.touched.full_name ? (
        <p className="ChatInfoAttributes__Content__Item__Error">
          {formik.errors.full_name}
        </p>
      ) : null}
      {/* maxLength truncates an over-long paste silently; say so. Pasting a longer
          list of names into this field is exactly how the 255 cap gets hit. */}
      <PasteTruncatedWarning
        visible={namePaste.truncated}
        max={MAX_LEAD_TEXT_FIELD_LENGTH}
      />
      {ShowTotalAccess ? (
        <div className="ChatInfoAttributes__Content__Item">
          <p>{t('attributes.handle')}:</p>
          <Input
            color="ghost"
            size="sm"
            name="handle"
            value={formik.values.handle}
            className="ChatInfoAttributes__Content__Item__Value"
          />
        </div>
      ) : null}
      <div className="ChatInfoAttributes__Content__Item">
        <p>{t('attributes.email')}:</p>
        <Input
          color="ghost"
          size="sm"
          // Same bug as full_name had: `max` is inert on this input type, so it
          // never capped anything. email also carries a server-side max:255.
          maxLength={MAX_LEAD_TEXT_FIELD_LENGTH}
          name={'email'}
          onChange={(event) => {
            formik.handleChange(event);
          }}
          type={'email'}
          value={formik.values.email}
          placeholder={'example@gmail.com'}
          className="ChatInfoAttributes__Content__Item__Value"
        />
      </div>
      {formik.errors.email && formik.touched.email ? (
        <p className="ChatInfoAttributes__Content__Item__Error">
          {formik.errors.email}
        </p>
      ) : null}
      <div className="ChatInfoAttributes__Content__Item">
        <p>{t('attributes.value')}:</p>

        <NumericFormat
          customInput={Input}
          className="input input-xs border-none focus:outline-none text-right text-indigo-400"
          value={value}
          prefix="$"
          decimalSeparator="."
          thousandSeparator=","
          onFocus={() => {
            valueOnFocusRef.current = formik.values.value;
          }}
          onValueChange={(values) => {
            formik.setFieldValue('value', values.floatValue);
          }}
          onBlur={() => {
            // Fire on commit (blur) and only when the value actually changed:
            // onValueChange fires per keystroke, and a focus/blur with no edit
            // must not emit. ~750k events/mo of noise removed.
            if (formik.values.value === valueOnFocusRef.current) return;
            trackEvent('front.chat.attributes.update.value.change', {
              displayName: 'Modificar valor',
              description: 'Con qué frecuencia se modifica el valor del lead',
              ctaType: getModuleName(),
            });
          }}
        />
      </div>

      {leadCloseEnabled && selectedLead ? (
        <LeadClosePanelRows lead={toCloseLeadRef(selectedLead)} />
      ) : (
        <div className="ChatInfoAttributes__Content__Item">
          <p>{t('attributes.revenue')}:</p>
          <NumericFormat
            customInput={Input}
            className="input input-xs border-none focus:outline-none text-right text-indigo-400"
            value={revenue}
            prefix="$"
            decimalSeparator="."
            thousandSeparator=","
            onFocus={() => {
              revenueOnFocusRef.current = formik.values.revenue;
            }}
            onValueChange={(values) => {
              formik.setFieldValue('revenue', values.floatValue);
            }}
            onBlur={() => {
              // Fire on commit (blur) and only when the revenue actually changed:
              // onValueChange fires per keystroke, and a focus/blur with no edit
              // must not emit. ~250k events/mo of noise removed.
              if (formik.values.revenue === revenueOnFocusRef.current) return;
              trackEvent('front.chat.attributes.update.revenue.change', {
                displayName: 'Modificar ganancia',
                description: 'Con qué frecuencia se modifica el valor del lead',
                ctaType: getModuleName(),
              });
            }}
          />
        </div>
      )}
      <div className="ChatInfoAttributes__Content__Item">
        <p>{t('attributes.company')}:</p>
        <Input
          color="ghost"
          size="sm"
          // Same bug as full_name had: `max` is inert on a text input. company
          // also carries a server-side max:255.
          maxLength={MAX_LEAD_TEXT_FIELD_LENGTH}
          type="text"
          name={'company'}
          onChange={formik.handleChange}
          value={formik.values.company}
          placeholder={t('attributes.companyLabel')}
          className="ChatInfoAttributes__Content__Item__Value"
        />
      </div>
      {formik.errors.company && formik.touched.company ? (
        <p className="ChatInfoAttributes__Content__Item__Error">
          {formik.errors.company}
        </p>
      ) : null}
      <div className="ChatInfoAttributes__Content__Tags">
        <p>{t('attributes.tags')}:</p>
        <TagTypeahead
          assignedTags={(selectedLead?.tags || []).map((tag) =>
            getTagName(tag as tTagEntry),
          )}
          inputRef={tagInputRef}
          value={tagQuery}
          onChange={setTagQuery}
          onSelect={onTagSelect}
        />
      </div>
      <div className="ChatInfoAttributes__Content__Tags__Container">
        {selectedLead?.tags?.map((tag) => {
          const tagName = getTagName(tag as tTagEntry);
          return tagName ? <TagButton key={tagName} tag={tagName} /> : null;
        })}
      </div>
      {showSave ? (
        <div className="sticky bottom-0 z-10 bg-base-100 border-t border-neutral/40 px-2 py-2">
          <Button
            size="sm"
            type="submit"
            disabled={formik.isSubmitting}
            color="primary"
            className="w-full"
          >
            {formik.isSubmitting ? (
              <Loading size="sm" />
            ) : (
              t('attributes.saveButton')
            )}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
